import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clipboard,
  Clock,
  CreditCard,
  Download,
  FileCheck2,
  FileText,
  Film,
  Image as ImageIcon,
  MapPin,
  MessageSquare,
  ShieldCheck,
  Star,
  Users,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';
import { FormEvent, useMemo, useState, useEffect } from 'react';
import { isEvidenceVideo, resolveStorageImageUrl } from '../../../lib/imageUrl';
import EvidenceVideoPlayer from '../../../features/project-management/components/EvidenceVideoPlayer';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OwnerFinanceTab } from '../../components/OwnerFinanceTab';
import toast from 'react-hot-toast';
import {
  formatCurrency,
  getStatusLabel,
  type OwnerBid,
  type OwnerBidDocumentCategory,
  type OwnerProject,
  type OwnerProjectDocument,
} from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import {
  manproQueryKeys,
  ownerProjectService,
  ownerMonitoringService,
  type OwnerProjectBidDto,
  type OwnerProjectDto,
} from '../../../api/manpro';
import { useCreateChannel } from '../../../hooks/useChat';
import Modal from '../../../components/Modal';
import ReceiptPreviewModal, { type ReceiptPreview } from '../../../components/ReceiptPreviewModal';

type TenderTabType = 'ringkasan' | 'penawaran' | 'dokumen';
type ManproTabType = 'ringkasan' | 'milestone' | 'foto' | 'keuangan' | 'dokumen' | 'persetujuan';

type DailyPhoto = {
  id: string;
  title: string;
  category: string;
  reporter: string;
  locationTag: string;
  timestamp: string;
  url: string;
  note?: string;
  /** V8 — jangan render video sebagai <img> */
  mediaType?: 'photo' | 'video';
};



const getProjectImage = (id: string) => {
  const images = [
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=1200',
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=1200',
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&q=80&w=1200',
  ];
  const index = Math.abs(id.split('').reduce((total, char) => total + char.charCodeAt(0), 0)) % images.length;
  return images[index];
};

const isContingencyExpenseApproval = (approval: { type?: string; title?: string }) => {
  const title = (approval.title || '').toLowerCase();
  const type = (approval.type || '').toLowerCase();
  if (type === 'expense_approval') return true;
  if (type === 'other' && (title.includes('biaya tak terduga') || title.includes('pengeluaran rab') || title.includes('pengeluaran r ab'))) return true;
  return title.includes('biaya tak terduga');
};

const hasRabSnapshot = (approval: { type?: string; data?: Record<string, any> | null }) => {
  if (approval.type !== 'rab' || !approval.data) return false;
  return Boolean(
    approval.data.budget ||
    approval.data.milestones ||
    approval.data.allocation_contingency ||
    approval.data.budget_allocations ||
    approval.data.termijns,
  );
};

const formatDate = (value?: string) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const getProviderName = (bid: OwnerProjectBidDto) =>
  bid.provider?.company_name ||
  bid.provider?.name ||
  bid.provider?.contact_person_name ||
  'Provider';

const mapApiBidToOwnerBid = (bid: OwnerProjectBidDto): OwnerBid => ({
  id: bid.id,
  providerId: bid.provider_id,
  userId: bid.provider?.user_id,
  providerName: getProviderName(bid),
  amount: bid.amount ?? bid.offered_price ?? 0,
  duration: bid.duration || '-',
  note: bid.note || bid.provider_note || 'Provider mengirim penawaran untuk proyek ini.',
  methodStatement: bid.method_statement || undefined,
  documents: (bid.documents ?? []).map((document) => ({
    id: document.id,
    name: document.name,
    size: document.file_size ?? 0,
    type: document.file_type || 'application/octet-stream',
    uploadedAt: document.created_at || new Date().toISOString(),
    category: (document.category || 'other') as OwnerBidDocumentCategory,
  })),
  submittedAt: bid.submitted_at || undefined,
  status: bid.status === 'accepted' ? 'accepted' : bid.status === 'rejected' || bid.status === 'denied' ? 'rejected' : 'submitted',
});

const mapApiProjectToOwnerProject = (
  project: OwnerProjectDto,
  bids: OwnerProjectBidDto[],
  overallProgress?: number,
  manproStatus?: string
): OwnerProject => {
  const mappedBids = bids.map(mapApiBidToOwnerBid);
  const selectedBid = mappedBids.find((bid) => bid.status === 'accepted');

  return {
    id: project.id,
    title: project.title,
    category: project.category || '-',
    location: project.location || project.city || '-',
    addressDetail: project.address_detail || '-',
    latitude: project.latitude || undefined,
    longitude: project.longitude || undefined,
    budgetCeiling: project.budget_max ?? project.budget_min ?? 0,
    budgetRange: project.budget_range || undefined,
    procurementType: project.procurement_type || undefined,
    province: project.province || undefined,
    city: project.city || undefined,
    district: project.district || undefined,
    postalCode: project.postal_code || undefined,
    bidDeadline: project.bid_deadline || undefined,
    estimatedStart: project.estimated_start || undefined,
    estimatedFinish: project.estimated_finish || undefined,
    duration: project.duration || undefined,
    description: project.description || '-',
    requirements: project.requirements || [],
    manproProject: project.manproProject || project.manpro_project || null,
    manpro_project: project.manproProject || project.manpro_project || null,
    manproProjectId: project.manpro_project_id || undefined,
    documents: (project.documents ?? []).map<OwnerProjectDocument>((document) => ({
      id: document.id,
      name: document.name,
      size: document.file_size ?? 0,
      type: document.file_type || 'application/octet-stream',
      uploadedAt: document.created_at || new Date().toISOString(),
      filePath: document.file_path ?? undefined,
      category: document.document_category ?? undefined,
    })),
    status: (manproStatus === 'completed' || project.manproProject?.status === 'completed' || project.manpro_project?.status === 'completed') ? 'completed' : (project.status === 'cancelled' ? 'completed' : project.status),
    progress: overallProgress !== undefined ? overallProgress : (typeof project.progress_percent === 'number' && project.progress_percent >= 0 ? project.progress_percent : (project.status === 'converted_to_manpro' ? 60 : project.status === 'proposal_review' ? 10 : 0)),
    selectedProvider: selectedBid?.providerName,
    createdAt: project.created_at || new Date().toISOString(),
    bids: mappedBids,
    messages: [],
  };
};



const ProyekSayaDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tenderTab, setTenderTab] = useState<TenderTabType>('penawaran');
  const [manproTab, setManproTab] = useState<ManproTabType>('ringkasan');
  const [selectedPhoto, setSelectedPhoto] = useState<DailyPhoto | null>(null);
  const [selectedBidDetail, setSelectedBidDetail] = useState<OwnerBid | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  // Rejection modal state
  const [rejectionModalState, setRejectionModalState] = useState<{ isOpen: boolean; approvalId: string; reason: string }>({
    isOpen: false,
    approvalId: '',
    reason: '',
  });

  // Photo gallery state
  const [photoFilter, setPhotoFilter] = useState<'semua' | 'teknisi' | 'owner'>('semua');
  const [uploadModalState, setUploadModalState] = useState<{
    isOpen: boolean;
    title: string;
    location: string;
    description: string;
    file: File | null;
    isUploading: boolean;
  }>({
    isOpen: false,
    title: '',
    location: '',
    description: '',
    file: null,
    isUploading: false,
  });

  // Snapshot accordion state
  const [expandedApprovalId, setExpandedApprovalId] = useState<string | null>(null);
  const [expandedMilestoneId, setExpandedMilestoneId] = useState<number | string | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<ReceiptPreview>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalPayload, setApprovalPayload] = useState<{ id: string; status: 'Approved' | 'Rejected' } | null>(null);

  const {
    projects,
    acceptBid: acceptBidAction,
    rejectBid: rejectBidAction,
    isAcceptingBid,
    isRejectingBid,
    isLoadingProjects,
  } = useOwnerManpro();

  const createChannelMutation = useCreateChannel();

  const handleChatVendor = (vendorId: string | undefined, projectId: string | undefined) => {
    if (!vendorId) return;

    createChannelMutation.mutate(
      { to_user: vendorId, reference_id: projectId },
      {
        onSuccess: (data) => {
          navigate(`/owner/pesan?channel_id=${data.id}`);
        },
        onError: (error: any) => {
          console.error('Chat vendor error:', error);
          const errMsg = error?.response?.data?.message || error?.message || 'Terjadi kesalahan sistem.';
          toast.error(`Gagal membuka obrolan: ${errMsg}`);
        }
      }
    );
  };

  const detailProjectQuery = useQuery({
    queryKey: projectId ? manproQueryKeys.ownerProject(projectId) : ['manpro', 'owner', 'projects', 'missing-id'],
    queryFn: () => ownerProjectService.getOwnerProject(projectId as string),
    enabled: Boolean(projectId),
  });

  const monitoringQuery = useQuery({
    queryKey: projectId ? manproQueryKeys.ownerMonitoring(projectId) : [],
    queryFn: () => ownerMonitoringService.getOwnerMonitoring(projectId as string),
    enabled: Boolean(projectId),
  });


  const reportsQuery = useQuery({
    queryKey: projectId ? manproQueryKeys.ownerReports(projectId) : [],
    queryFn: () => ownerMonitoringService.getOwnerReports(projectId as string),
    enabled: Boolean(projectId) && manproTab == "foto",
  });

  const approvalsQuery = useQuery({
    queryKey: projectId ? manproQueryKeys.ownerApprovals(projectId) : [],
    queryFn: () => ownerMonitoringService.getOwnerApprovals(projectId as string),
    enabled: Boolean(projectId),
  });

  console.log("Monitoring Query Data:", monitoringQuery.data);
  console.log("Approvals Query Data:", approvalsQuery.data);

  const approveMutation = useMutation({
    mutationFn: (approvalId: string) => ownerMonitoringService.approveOwnerApproval(projectId as string, approvalId),
    onSuccess: () => {
      toast.success('Persetujuan berhasil disimpan.');
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerApprovals(projectId as string) });
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerMonitoring(projectId as string) });
      detailProjectQuery.refetch();
    },
    onError: () => toast.error('Gagal menyetujui, silakan coba lagi.'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ approvalId, reason }: { approvalId: string; reason: string }) =>
      ownerMonitoringService.rejectOwnerApproval(projectId as string, approvalId, reason),
    onSuccess: () => {
      toast.success('Penolakan berhasil dikirim.');
      setRejectionModalState({ isOpen: false, approvalId: '', reason: '' });
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerApprovals(projectId as string) });
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerMonitoring(projectId as string) });
    },
    onError: () => toast.error('Gagal menolak, silakan coba lagi.'),
  });

  const finalizeProjectMutation = useMutation({
    mutationFn: (id: string) => ownerProjectService.finalizeOwnerProject(id),
    onSuccess: () => {
      toast.success('Persetujuan finalisasi proyek berhasil dikirim!');
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerProject(projectId as string) });
      queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'projects'] });
      detailProjectQuery.refetch();
    },
    onError: () => toast.error('Gagal memfinalisasi proyek, silakan coba lagi.'),
  });

  const rejectHandoverMutation = useMutation({
    mutationFn: (id: string) => ownerProjectService.rejectHandover(id),
    onSuccess: () => {
      toast.success('Pengajuan BAST berhasil ditolak. Proyek dikembalikan ke Provider.');
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerProject(projectId as string) });
      queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'projects'] });
      detailProjectQuery.refetch();
    },
    onError: () => toast.error('Gagal menolak BAST, silakan coba lagi.'),
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: (data: FormData) => ownerMonitoringService.uploadOwnerPhoto(projectId as string, data),
    onSuccess: () => {
      toast.success('Foto temuan berhasil diunggah.');
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerReports(projectId as string) });
      setUploadModalState(prev => ({ ...prev, isOpen: false, file: null, title: '', location: '', description: '' }));
    },
    onError: () => toast.error('Gagal mengunggah foto, pastikan ukuran tidak lebih dari 10MB.'),
    onSettled: () => setUploadModalState(prev => ({ ...prev, isUploading: false })),
  });

  const project = useMemo(
    () => {
      const sourceProject = detailProjectQuery.data ?? projects.find((item) => item.id === projectId);
      if (!sourceProject) return undefined;

      const projectBids = sourceProject.bids?.length
        ? sourceProject.bids
        : [];

      return mapApiProjectToOwnerProject(sourceProject, projectBids, monitoringQuery.data?.overall_progress, monitoringQuery.data?.status);
    },
    [detailProjectQuery.data, projectId, projects, monitoringQuery.data?.overall_progress, monitoringQuery.data?.status],
  );

  const formatPublicUrl = (url: string) => {
    if (!url) return url;
    if (url.includes('indoteknisi.net') && !url.includes('/public/')) {
      return url.replace('indoteknisi.net/storage', 'indoteknisi.net/public/storage');
    } else if (!url.startsWith('http') && !url.startsWith('data:')) {
      const cleanPath = url.startsWith('/') ? url.slice(1) : url;
      const storagePath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
      return `https://indoteknisi.net/public/${storagePath}`;
    }
    return url;
  };

  const dailyPhotos = useMemo<DailyPhoto[]>(() => {
    const apiReports = reportsQuery.data?.reports || [];
    const extractedPhotos: DailyPhoto[] = [];

    const resolveMediaType = (item: any, url: string): 'photo' | 'video' => {
      if (item?.media_type === 'video' || item?.type === 'video') return 'video';
      if (item?.media_type === 'photo' || item?.type === 'photo') return 'photo';
      return isEvidenceVideo(typeof item === 'string' ? item : { url, path: item?.path, type: item?.media_type })
        ? 'video'
        : 'photo';
    };

    apiReports.forEach((report, rIdx) => {
      if (report.photos && report.photos.length > 0) {
        report.photos.forEach((photoItem, pIdx) => {
          if (typeof photoItem === 'string') {
            const url = formatPublicUrl(photoItem);
            extractedPhotos.push({
              id: `report-photo-${rIdx}-${pIdx}`,
              title: report.title || `Dokumentasi #${rIdx + 1}`,
              category: report.category || 'Dokumentasi Lapangan',
              reporter: report.reporter_name || 'Teknisi MANPRO',
              locationTag: report.location_tag || 'Lokasi Proyek',
              timestamp: report.date || 'Terbaru',
              url,
              note: report.description,
              mediaType: resolveMediaType(photoItem, url),
            });
          } else if (typeof photoItem === 'object' && photoItem?.url) {
            const url = formatPublicUrl(photoItem.url);
            extractedPhotos.push({
              id: photoItem.id || `report-photo-${rIdx}-${pIdx}`,
              title: photoItem.caption || report.title || `Dokumentasi #${rIdx + 1}`,
              category: photoItem.category || report.category || 'Dokumentasi Lapangan',
              reporter: photoItem.reporter_name || report.reporter_name || 'Teknisi MANPRO',
              locationTag: photoItem.location_tag || report.location_tag || 'Lokasi Proyek',
              timestamp: photoItem.uploaded_at || report.date || 'Terbaru',
              url,
              note: report.description,
              mediaType: resolveMediaType(photoItem, url),
            });
          }
        });
      } else if (report.photo_urls && report.photo_urls.length > 0) {
        report.photo_urls.forEach((url, pIdx) => {
          const publicUrl = formatPublicUrl(url);
          extractedPhotos.push({
            id: `report-url-${rIdx}-${pIdx}`,
            title: report.title || `Dokumentasi #${rIdx + 1}`,
            category: report.category || 'Dokumentasi Lapangan',
            reporter: report.reporter_name || 'Teknisi MANPRO',
            locationTag: report.location_tag || 'Lokasi Proyek',
            timestamp: report.date || 'Terbaru',
            url: publicUrl,
            note: report.description,
            mediaType: resolveMediaType(url, publicUrl),
          });
        });
      } else if (report.image_url) {
        const url = formatPublicUrl(report.image_url);
        extractedPhotos.push({
          id: `report-img-${rIdx}`,
          title: report.title,
          category: report.category || 'Dokumentasi Lapangan',
          reporter: report.reporter_name || 'Teknisi MANPRO',
          locationTag: report.location_tag || 'Lokasi Proyek',
          timestamp: report.date || 'Terbaru',
          url,
          note: report.description,
          mediaType: resolveMediaType(report.image_url, url),
        });
      }
    });

    // Apply filter
    return extractedPhotos.filter(photo => {
      if (photoFilter === 'teknisi') return photo.category !== 'Temuan Owner';
      if (photoFilter === 'owner') return photo.category === 'Temuan Owner';
      return true; // 'semua'
    });
  }, [reportsQuery.data, photoFilter]);

  const selectedBid = project?.bids?.find((bid) => bid.status === 'accepted');
  const hasAcceptedProvider = Boolean(selectedBid || project?.selectedProvider);

  // PERBEDAAN MODE TAMPILAN BERDASARKAN LIFE CYCLE:
  // Mode Monitoring (Proyek Berjalan) HANYA AKTIF jika ada provider yang sudah diterima atau status sudah 'converted_to_manpro'.
  const isMonitoringMode =
    hasAcceptedProvider || (project?.status && ['accepted', 'converted_to_manpro', 'in_delivery', 'completed'].includes(project.status));

  const totalContract = selectedBid?.amount ?? project?.budgetCeiling ?? 150000000;


  const stepperPhases = useMemo(() => {
    if (monitoringQuery.data?.monitoring_stages && monitoringQuery.data.monitoring_stages.length > 0) {
      return monitoringQuery.data.monitoring_stages.map((stage: any, idx: number) => {
        let status = 'upcoming';
        if (stage.done) status = 'done';
        else if (idx === 0 || monitoringQuery.data.monitoring_stages[idx - 1]?.done) status = 'active';

        return {
          label: stage.title,
          status: status,
          desc: stage.description || (status === 'active' ? `Progres ${project?.progress || 0}%` : 'Tahap berikut'),
          activePercent: status === 'active' ? project?.progress : undefined,
        };
      });
    }

    return [
      { label: 'Tahap Tender', status: 'done', desc: 'Selesai' },
      { label: 'Persiapan', status: 'active', desc: 'Menunggu Data API', activePercent: project?.progress || 0 },
      { label: 'Eksekusi', status: 'upcoming', desc: 'Tahap berikut' },
    ];
  }, [monitoringQuery.data?.monitoring_stages, project?.progress]);

  const acceptBid = async (bidId: string) => {
    if (project) {
      await acceptBidAction(project.id, bidId);
    }
  };

  const handleUploadPhoto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadModalState.file || !uploadModalState.title) return;

    setUploadModalState(prev => ({ ...prev, isUploading: true }));
    const formData = new FormData();
    formData.append('photo', uploadModalState.file);
    formData.append('title', uploadModalState.title);
    if (uploadModalState.location) formData.append('location', uploadModalState.location);
    if (uploadModalState.description) formData.append('description', uploadModalState.description);

    uploadPhotoMutation.mutate(formData);
  };

  const locationPhotos = project?.documents?.filter((doc) => doc.category === 'location_photo') || [];
  const bannerPhotos = locationPhotos.length > 0
    ? locationPhotos.map(doc => resolveStorageImageUrl(doc.filePath))
    : project ? [getProjectImage(project.id)] : [];

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? bannerPhotos.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === bannerPhotos.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (bannerPhotos.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev === bannerPhotos.length - 1 ? 0 : prev + 1));
    }, 1000); // 1 detik

    return () => clearInterval(interval);
  }, [bannerPhotos.length]);

  if (!project) {
    const isLoading = isLoadingProjects || detailProjectQuery.isLoading;

    if (isLoading) {
      return (
        <div className="space-y-6 animate-pulse">
          {/* Skeleton Navigation Link */}
          <div className="h-5 w-40 rounded-lg bg-slate-200" />

          {/* Skeleton Hero Banner Header */}
          <div className="relative h-64 sm:h-72 w-full rounded-3xl bg-slate-200 overflow-hidden shadow-sm">
            <div className="absolute bottom-6 left-6 right-6 space-y-3">
              <div className="flex gap-2">
                <div className="h-6 w-36 rounded-full bg-slate-300/80" />
                <div className="h-6 w-24 rounded-full bg-slate-300/80" />
              </div>
              <div className="h-8 w-2/3 rounded-xl bg-slate-300/90" />
              <div className="h-4 w-1/2 rounded-lg bg-slate-300/70" />
            </div>
          </div>

          {/* Skeleton Metrics Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="h-28 rounded-2xl bg-slate-200/80" />
            <div className="h-28 rounded-2xl bg-slate-200/80" />
            <div className="h-28 rounded-2xl bg-slate-200/80" />
          </div>

          {/* Skeleton Main Section */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4 shadow-sm">
            <div className="h-6 w-48 rounded-lg bg-slate-200" />
            <div className="h-12 w-full rounded-xl bg-slate-100" />
            <div className="h-12 w-full rounded-xl bg-slate-100" />
            <div className="h-12 w-full rounded-xl bg-slate-100" />
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
        <FileText size={40} className="mx-auto text-slate-300" />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">
          Proyek tidak ditemukan
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Proyek yang Anda cari tidak ada atau Anda tidak memiliki akses.
        </p>
        <Link to="/owner/proyek-saya" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700">
          Kembali ke Proyek Saya
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/owner/proyek-saya" className="inline-flex items-center gap-2 text-sm font-extrabold text-blue-700">
        <ArrowLeft size={16} />
        Kembali ke Proyek Saya
      </Link>

      {/* Hero Banner Header */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="group relative h-64 sm:h-72">
          <img
            src={bannerPhotos[currentImageIndex]}
            alt={project.title}
            className="h-full w-full object-cover transition-all duration-500"
          />
          {bannerPhotos.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white backdrop-blur hover:bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white backdrop-blur hover:bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight size={24} />
              </button>
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 z-10">
                {bannerPhotos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`h-2 rounded-full transition-all ${currentImageIndex === idx ? 'w-6 bg-blue-500' : 'w-2 bg-white/50 hover:bg-white/80'
                      }`}
                  />
                ))}
              </div>
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-extrabold text-white ${isMonitoringMode ? 'bg-indigo-600' : 'bg-blue-600'}`}>
                  {isMonitoringMode ? 'Proyek Berjalan (Live MANPRO)' : 'Tahap Tender & Review Penawaran'}
                </span>
                <span className="rounded-full bg-slate-900/80 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
                  {getStatusLabel(project.status)}
                </span>
              </div>
              <h1 className="mt-3 max-w-4xl text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
                {project.title}
              </h1>
              <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-blue-100 sm:text-sm">
                <MapPin size={16} />
                {project.location} • {project.category}
              </p>
            </div>

            <div className="shrink-0 rounded-2xl bg-white/15 p-4 ring-1 ring-white/20 backdrop-blur text-white">
              <p className="text-xs font-medium text-blue-100">{isMonitoringMode ? 'Nilai Kontrak RAB' : 'Plafon Budget Tender'}</p>
              <p className="text-xl font-extrabold sm:text-2xl">{formatCurrency(totalContract)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* BANNER 1: JIKA MODAL/PROYEK MASIH TAHAP TENDER (Belum Diterima) */}
      {!isMonitoringMode && (
        <section className="rounded-3xl border border-blue-100 bg-gradient-to-r from-blue-700 to-indigo-700 p-6 text-white shadow-lg">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold text-white">
                <FileText size={15} /> Mode Review Penawaran Vendor
              </span>
              <h2 className="mt-2 text-xl font-extrabold text-white">
                {project.bids.length > 0
                  ? `${project.bids.length} Vendor Telah Mengirimkan Proposal Penawaran`
                  : 'Menunggu Penawaran Masuk dari Vendor'}
              </h2>
              <p className="mt-1 text-xs text-blue-100">
                Bandingkan proposal penawaran harga, estimasi waktu, dan dokumen vendor di bawah ini. Tekan "Pilih & Terima Vendor" untuk memulai eksekusi proyek.
              </p>
            </div>
            {project.bids.length > 0 && (
              <button
                type="button"
                onClick={() => setTenderTab('penawaran')}
                className="shrink-0 rounded-xl bg-white px-5 py-3 text-xs font-extrabold text-blue-700 shadow-sm hover:bg-blue-50"
              >
                Review Proposal ({project.bids.length})
              </button>
            )}
          </div>
        </section>
      )}

      {/* BANNER 1.4: JIKA PROYEK MENUNGGU PERSETUJUAN RAB */}
      {isMonitoringMode && (project.manproProject?.status === 'pending_rab_approval' || project.manpro_project?.status === 'pending_rab_approval') && (
        <section className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-100 to-blue-50 p-6 text-blue-900 shadow-md mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-200 px-3 py-1 text-xs font-extrabold text-blue-800">
                <CheckCircle2 size={15} /> RAB / Baseline Diajukan
              </span>
              <h2 className="mt-2 text-xl font-extrabold text-blue-900">
                Menunggu Persetujuan RAB dari Anda
              </h2>
              <p className="mt-1 text-xs text-blue-800">
                Vendor telah mengajukan Rencana Anggaran Biaya (RAB) dan Baseline proyek. Silakan tinjau dan setujui untuk memulai pekerjaan.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* BANNER 1.5: JIKA PROYEK MENUNGGU FINALISASI BAST */}
      {isMonitoringMode && (project.manproProject?.status === 'pending_bast_approval' || project.manpro_project?.status === 'pending_bast_approval' || project.manproProject?.status === 'pending_owner_approval' || project.manpro_project?.status === 'pending_owner_approval') && (
        <section className="rounded-3xl border border-amber-200 bg-gradient-to-r from-amber-100 to-amber-50 p-6 text-amber-900 shadow-md mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-200 px-3 py-1 text-xs font-extrabold text-amber-800">
                <CheckCircle2 size={15} /> Pekerjaan Selesai 100%
              </span>
              <h2 className="mt-2 text-xl font-extrabold text-amber-900">
                Menunggu Persetujuan Final (Serah Terima)
              </h2>
              <p className="mt-1 text-xs text-amber-800">
                Vendor telah menyelesaikan semua pekerjaannya. Silakan periksa hasil pekerjaan dan setujui untuk menutup proyek ini.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (projectId && window.confirm("Anda yakin ingin menolak pengajuan BAST ini? Status proyek akan dikembalikan ke Provider untuk revisi.")) {
                    rejectHandoverMutation.mutate(projectId);
                  }
                }}
                disabled={rejectHandoverMutation.isPending || finalizeProjectMutation.isPending}
                className="shrink-0 rounded-xl border-2 border-red-200 bg-white px-5 py-2.5 text-sm font-extrabold text-red-600 shadow-sm hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {rejectHandoverMutation.isPending ? 'Menolak...' : 'Tolak BAST'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (projectId) {
                    finalizeProjectMutation.mutate(projectId);
                  }
                }}
                disabled={finalizeProjectMutation.isPending || rejectHandoverMutation.isPending}
                className="shrink-0 rounded-xl bg-amber-600 px-6 py-3 text-sm font-extrabold text-white shadow-sm hover:bg-amber-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {finalizeProjectMutation.isPending ? 'Memproses...' : 'Setujui Finalisasi'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* BANNER 2: JIKA PROYEK SUDAH MANPRO (PROYEK BERJALAN) */}
      {isMonitoringMode && (
        <section className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 p-6 text-white shadow-xl">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-extrabold text-indigo-200">
                <ShieldCheck size={15} /> Live Project Monitoring MANPRO
              </span>
              <h2 className="mt-2 text-xl font-extrabold text-white">
                Progres Lapangan: {stepperPhases.find((p) => p.status === 'active')?.label || 'Eksekusi'} ({project.progress}%)
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-extrabold text-emerald-400">{project.progress}%</span>
              <span className="text-xs text-indigo-200">Total Progres</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-5">
            {stepperPhases.map((phase, idx) => {
              const isDone = phase.status === 'done';
              const isActive = phase.status === 'active';
              return (
                <div key={phase.label} className="relative flex flex-col justify-between rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur">
                  <div className="flex items-center justify-between">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold ${isDone
                        ? 'bg-emerald-500 text-white'
                        : isActive
                          ? 'bg-indigo-500 text-white ring-4 ring-indigo-300/40'
                          : 'bg-white/20 text-indigo-200'
                        }`}
                    >
                      {isDone ? <Check size={16} /> : idx + 1}
                    </span>
                    {isActive && (
                      <span className="animate-pulse rounded-full bg-emerald-400 px-2 py-0.5 text-[10px] font-extrabold text-slate-900">
                        In Progress
                      </span>
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-extrabold text-white">{phase.label}</p>
                    <p className="mt-1 text-[11px] text-indigo-200">{phase.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* SUB-TABS NAVIGATION UNTUK TENDER MODE (3 TABS) vs MANPRO MODE (5 TABS) */}
      <section className="border-b border-slate-200 bg-white shadow-sm rounded-2xl p-2">
        <nav className="flex gap-2 overflow-x-auto no-scrollbar">
          {!isMonitoringMode ? (
            // SUB-TABS MODE TENDER / REVIEW PENAWARAN
            [
              { id: 'penawaran', label: `📩 Penawaran Vendor (${project.bids.length})`, icon: FileText, badge: 'Utama' },
              { id: 'ringkasan', label: '📌 Detail Scope & Syarat Proyek', icon: Building2 },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = tenderTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTenderTab(tab.id as TenderTabType)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-3 text-xs font-extrabold transition-all ${isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-100'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })
          ) : (
            // SUB-TABS MODE MANPRO PROYEK BERJALAN
            [
              { id: 'ringkasan', label: '📌 Ringkasan & Kontraktor', icon: Building2 },
              { id: 'milestone', label: '🚧 Progress & Milestone Checkpoint', icon: CheckCircle2 },
              { id: 'foto', label: '📷 Foto Lapangan (Daily Stream)', icon: Camera, badge: '4 Foto' },
              { id: 'persetujuan', label: '📋 Persetujuan Proyek', icon: FileCheck2 },
              { id: 'keuangan', label: '💳 Keuangan & Termin Pembayaran', icon: Wallet, alert: true },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = manproTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setManproTab(tab.id as ManproTabType)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-3 text-xs font-extrabold transition-all ${isActive
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })
          )}
        </nav>
      </section>

      {/* CONTENT MAIN BODY */}
      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          {/* ====================================================== */}
          {/* SECTION A: TAMPILAN KHUSUS TAHAP TENDER / REVIEW BID */}
          {/* ====================================================== */}
          {!isMonitoringMode && (
            <>
              {/* TAB PENAWARAN VENDOR MASUK (TENDER MODE) */}
              {tenderTab === 'penawaran' && (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-4">
                      <div>
                        <h2 className="text-lg font-extrabold text-slate-900">Review & Pilih Penawaran Vendor</h2>
                        <p className="mt-1 text-xs text-slate-500">
                          Evaluasi harga, estimasi durasi, dan catatan metode dari vendor kontraktor.
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700">
                        Total {project.bids.length} Penawaran
                      </span>
                    </div>

                    <div className="mt-5 space-y-4">
                      {project.bids.map((bid) => (
                        <article
                          key={bid.id}
                          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
                        >
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-3 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-extrabold text-blue-800">
                                  {bid.providerName.slice(0, 2).toUpperCase()}
                                </span>
                                <h3 className="text-base font-extrabold text-slate-900">{bid.providerName}</h3>
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                                  <Star size={13} className="fill-amber-400 text-amber-400" /> 4.9 (24 Proyek)
                                </span>
                                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-extrabold text-emerald-700">
                                  Vendor Terverifikasi
                                </span>
                              </div>

                              <p className="text-xs leading-6 text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                <span className="font-extrabold text-slate-800 block mb-1">Catatan Proposal:</span>
                                "{bid.note}"
                              </p>

                              <div className="flex flex-wrap gap-4 text-xs font-extrabold text-slate-800">
                                <div className="rounded-xl bg-blue-50 px-3 py-2 text-blue-800">
                                  Harga Penawaran: <span className="text-sm">{formatCurrency(bid.amount)}</span>
                                </div>
                                <div className="rounded-xl bg-slate-100 px-3 py-2 text-slate-700">
                                  Estimasi Durasi: <span className="text-sm">{bid.duration}</span>
                                </div>
                              </div>
                            </div>

                            <div className="shrink-0 flex flex-col gap-2 min-w-[170px]">
                              <Link
                                to={`/owner/penawaran-masuk/${project.id}/${bid.id}`}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-extrabold text-blue-800 hover:bg-blue-100 transition-all"
                              >
                                <FileText size={14} />
                                Lihat Detail Bids
                              </Link>

                              {bid.status === 'submitted' ? (
                                <button
                                  type="button"
                                  disabled={isAcceptingBid}
                                  onClick={() => acceptBid(bid.id)}
                                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-extrabold text-white shadow-md shadow-blue-100 hover:bg-blue-700 active:scale-95 disabled:opacity-50"
                                >
                                  <ShieldCheck size={15} />
                                  Pilih & Terima Vendor
                                </button>
                              ) : (
                                <span className="rounded-xl bg-emerald-100 p-2.5 text-center text-xs font-extrabold text-emerald-800">
                                  ✓ Vendor Terpilih
                                </span>
                              )}

                              <button
                                type="button"
                                onClick={() => handleChatVendor(bid.userId || bid.providerId, project.id)}
                                disabled={createChannelMutation.isPending}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              >
                                <MessageSquare size={14} />
                                {createChannelMutation.isPending ? 'Membuka...' : 'Chat & Negosiasi'}
                              </button>

                              {bid.status === 'submitted' && (
                                <button
                                  type="button"
                                  disabled={isRejectingBid}
                                  onClick={() => rejectBidAction(project.id, bid.id)}
                                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-extrabold text-red-700 hover:bg-red-100 transition-all disabled:opacity-50"
                                >
                                  <XCircle size={14} />
                                  Tolak Penawaran
                                </button>
                              )}
                            </div>
                          </div>
                        </article>
                      ))}

                      {project.bids.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-xs text-slate-400">
                          <FileText size={36} className="mx-auto text-slate-300 mb-2" />
                          <p className="font-bold text-slate-700 text-sm">Belum ada penawaran vendor masuk.</p>
                          <p className="mt-1">Penawaran dari kontraktor terverifikasi akan muncul di sini.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB RINGKASAN TENDER (PENGADAAN LENGKAP) */}
              {tenderTab === 'ringkasan' && (
                <div className="space-y-6">
                  {/* Card 1: Informasi Utama & Pengadaan */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-extrabold text-slate-900">Spesifikasi & Detail Pengadaan Proyek</h2>
                    <p className="mt-1 text-xs text-slate-500">Seluruh parameter pengadaan yang ditentukan saat pemasangan proyek.</p>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        ['Judul Proyek', project.title],
                        ['Kategori Pekerjaan', project.category],
                        ['Plafon Budget Maksimal', formatCurrency(project.budgetCeiling)],
                        ['Tipe Pengadaan', project.procurementType || 'Tender Terbuka'],
                        ['Batas Akhir Penawaran', formatDate(project.bidDeadline)],
                        ['Estimasi Tanggal Mulai', formatDate(project.estimatedStart)],
                        ['Estimasi Tanggal Selesai', formatDate(project.estimatedFinish)],
                        ['Estimasi Durasi Kerja', project.duration || 'Sesuai Proposal Vendor'],
                        ['Tanggal Dibuat', formatDate(project.createdAt)],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                          <p className="text-[11px] font-extrabold text-slate-400">{label}</p>
                          <p className="mt-1 break-words text-xs font-bold text-slate-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Card 2: Lokasi & Detail Alamat */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-extrabold text-slate-900">Lokasi & Detail Alamat Pekerjaan</h2>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        ['Alamat Lengkap', project.addressDetail],
                        ['Provinsi', project.province || '-'],
                        ['Kota / Kabupaten', project.city || project.location || '-'],
                        ['Kecamatan', project.district || '-'],
                        ['Kode Pos', project.postalCode || '-'],
                        ['Koordinat Lokasi', project.latitude && project.longitude ? `${project.latitude}, ${project.longitude}` : 'Sudah terverifikasi di peta'],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                          <p className="text-[11px] font-extrabold text-slate-400">{label}</p>
                          <p className="mt-1 break-words text-xs font-bold text-slate-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Card 3: Uraian Deskripsi Kebutuhan */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-extrabold text-slate-900">Deskripsi Uraian Kebutuhan Proyek</h2>
                    <p className="mt-4 text-xs leading-6 text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-line">
                      {project.description || 'Tidak ada uraian deskripsi tambahan.'}
                    </p>
                  </div>

                  {/* Card 4: Scope & Syarat Pekerjaan Disyaratkan */}
                  {project.requirements && project.requirements.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                      <h2 className="text-lg font-extrabold text-slate-900">Scope Pekerjaan Disetujui</h2>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {project.requirements.map((item) => (
                          <div key={item} className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                            <p className="text-xs font-semibold leading-5 text-slate-700">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 5: Dokumen & Berkas SPK Kontrak */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-extrabold text-slate-900">Dokumen & Berkas SPK Kontrak</h2>
                    <div className="mt-4 space-y-3">
                      {project.documents?.map((doc: any) => (
                        <div key={doc.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 font-bold text-xs">
                              PDF
                            </div>
                            <div>
                              <p className="text-xs font-extrabold text-slate-800">{doc.name}</p>
                              <p className="mt-0.5 text-[10px] text-slate-400">Diunggah: {formatDate(doc.uploadedAt)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toast.success(`Mengunduh ${doc.name}...`)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-50"
                          >
                            <Download size={14} /> Unduh
                          </button>
                        </div>
                      ))}
                      {(!project.documents || project.documents.length === 0) && (
                        <p className="text-xs text-slate-400">Belum ada dokumen yang dilampirkan.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ====================================================== */}
          {/* SECTION B: TAMPILAN KHUSUS MODE MANPRO (PROYEK BERJALAN) */}
          {/* ====================================================== */}
          {isMonitoringMode && (
            <>
              {manproTab === 'ringkasan' && (
                <div className="space-y-8">
                  {/* Contract Information Card */}
                  <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/30 transition-all duration-300 hover:shadow-xl hover:shadow-slate-200/40">
                    <div className="border-b border-slate-200/80 bg-gradient-to-r from-slate-50/80 to-white px-7 py-5">
                      <div className="flex items-center justify-between">
                        <h2 className="flex items-center gap-2.5 text-base font-bold text-slate-800">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                            <FileText size={18} />
                          </span>
                          Informasi Kontrak & Kontraktor
                        </h2>
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                          Aktif
                        </span>
                      </div>
                    </div>

                    <div className="p-7">
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                          ['Judul Proyek', project.title],
                          ['Kontraktor', selectedBid?.providerName || 'PT Tekno Karya Interior'],
                          ['Nilai Kontrak', formatCurrency(totalContract)],
                          ['Durasi', project.duration || '60 Hari'],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className="group relative rounded-xl border border-slate-100 bg-slate-50/50 p-4 transition-all duration-200 hover:border-emerald-200 hover:bg-emerald-50/30"
                          >
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              {label}
                            </p>
                            <p className="mt-1.5 text-sm font-semibold text-slate-800">
                              {value}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2 lg:grid-cols-3">
                        {[
                          ['Alamat', project.addressDetail],
                          ['Kota / Provinsi', `${project.city || project.location}, ${project.province || '-'}`],
                          ['Tanggal Mulai', formatDate(project.estimatedStart)],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className="flex items-start gap-3 rounded-lg bg-slate-50/50 p-3.5"
                          >
                            <div className="mt-0.5 rounded-full bg-slate-200/70 p-1">
                              <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                {label}
                              </p>
                              <p className="mt-0.5 truncate text-xs font-medium text-slate-700">
                                {value}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {monitoringQuery.data?.contingency && (
                    <div className="overflow-hidden rounded-2xl border border-amber-200/80 bg-white shadow-lg shadow-slate-200/30">
                      <div className="border-b border-amber-100 bg-amber-50/60 px-7 py-5">
                        <h2 className="flex items-center gap-2.5 text-base font-bold text-slate-800">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                            <Wallet size={18} />
                          </span>
                          Dana Darurat (Kontingensi)
                        </h2>
                        <p className="mt-1 text-xs text-amber-800/80">
                          Plafon sudah disetujui di RAB. Provider mencatat pemakaian tanpa persetujuan per transaksi.
                        </p>
                      </div>
                      <div className="p-7">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Plafon</p>
                            <p className="mt-1 text-sm font-extrabold text-slate-900">
                              {formatCurrency(Number(monitoringQuery.data.contingency.allocated_amount || 0))}
                            </p>
                            <p className="mt-0.5 text-[11px] text-slate-500">{Number(monitoringQuery.data.contingency.percent || 0)}% dari RAB</p>
                          </div>
                          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Terpakai</p>
                            <p className="mt-1 text-sm font-extrabold text-slate-900">
                              {formatCurrency(Number(monitoringQuery.data.contingency.spent_amount || 0))}
                            </p>
                          </div>
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Sisa</p>
                            <p className="mt-1 text-sm font-extrabold text-emerald-900">
                              {formatCurrency(Number(monitoringQuery.data.contingency.remaining_amount || 0))}
                            </p>
                          </div>
                        </div>

                        <h3 className="mt-6 text-xs font-extrabold uppercase tracking-wider text-slate-500">Pemakaian kontingensi</h3>
                        <div className="mt-3 space-y-2">
                          {(monitoringQuery.data.contingency_expenses || []).length === 0 ? (
                            <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
                              Belum ada pemakaian dana darurat.
                            </p>
                          ) : (
                            (monitoringQuery.data.contingency_expenses || []).map((exp) => (
                              <div key={exp.id} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-slate-800">{exp.description || 'Pemakaian kontingensi'}</p>
                                  <p className="text-[11px] text-slate-500">{exp.date || '-'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  {exp.receipt_url ? (
                                    <button
                                      type="button"
                                      onClick={() => setReceiptPreview({
                                        url: exp.receipt_url as string,
                                        title: exp.description || 'Struk kontingensi',
                                      })}
                                      className="text-[11px] font-bold text-blue-700 hover:underline"
                                    >
                                      Lihat struk
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-slate-400">Tidak ada struk</span>
                                  )}
                                  <span className="text-sm font-extrabold text-red-600">{formatCurrency(Number(exp.amount || 0))}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {(() => {
                    const rabAllocations = (monitoringQuery.data?.allocations || []).filter((row) => !row.is_contingency);
                    const rabExpenses = monitoringQuery.data?.rab_expenses || [];
                    if (rabAllocations.length === 0 && rabExpenses.length === 0) return null;
                    return (
                    <div className="overflow-hidden rounded-2xl border border-blue-200/80 bg-white shadow-lg shadow-slate-200/30">
                      <div className="border-b border-blue-100 bg-blue-50/60 px-7 py-5">
                        <h2 className="flex items-center gap-2.5 text-base font-bold text-slate-800">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                            <Wallet size={18} />
                          </span>
                          Pengeluaran RAB
                        </h2>
                        <p className="mt-1 text-xs text-blue-800/80">
                          Realisasi pos anggaran yang sudah disetujui di RAB. Bukan antrean Setujui/Tolak.
                        </p>
                      </div>
                      <div className="p-7">
                        <div className="space-y-3">
                          {rabAllocations.map((row) => {
                            const over = Number(row.spent_amount || 0) > Number(row.allocated_amount || 0);
                            return (
                              <div key={String(row.id || row.name)} className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm font-bold text-slate-800">{row.name}</p>
                                  {over && <span className="text-[10px] font-extrabold uppercase text-red-600">Over anggaran</span>}
                                </div>
                                <p className="mt-1 text-xs text-slate-500">
                                  Terpakai {formatCurrency(Number(row.spent_amount || 0))} dari {formatCurrency(Number(row.allocated_amount || 0))}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                        <h3 className="mt-6 text-xs font-extrabold uppercase tracking-wider text-slate-500">Catatan pengeluaran RAB</h3>
                        <div className="mt-3 space-y-2">
                          {rabExpenses.length === 0 ? (
                            <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
                              Belum ada pengeluaran pos RAB.
                            </p>
                          ) : (
                            rabExpenses.map((exp) => (
                              <div key={exp.id} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-slate-800">{exp.description || 'Pengeluaran RAB'}</p>
                                  <p className="text-[11px] text-slate-500">{exp.category || '-'} · {exp.date || '-'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  {exp.receipt_url ? (
                                    <button
                                      type="button"
                                      onClick={() => setReceiptPreview({
                                        url: exp.receipt_url as string,
                                        title: exp.description || 'Struk pengeluaran RAB',
                                      })}
                                      className="text-[11px] font-bold text-blue-700 hover:underline"
                                    >
                                      Lihat struk
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-slate-400">Tidak ada struk</span>
                                  )}
                                  <span className="text-sm font-extrabold text-red-600">{formatCurrency(Number(exp.amount || 0))}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })()}

                  {/* Scope of Work Card */}
                  <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/30 transition-all duration-300 hover:shadow-xl hover:shadow-slate-200/40">
                    <div className="border-b border-slate-200/80 bg-gradient-to-r from-slate-50/80 to-white px-7 py-5">
                      <h2 className="flex items-center gap-2.5 text-base font-bold text-slate-800">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                          <Clipboard size={18} />
                        </span>
                        Scope Pekerjaan Disetujui
                        <span className="ml-auto rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
                          {project.requirements?.length || 0} item
                        </span>
                      </h2>
                    </div>

                    <div className="p-7">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {project.requirements?.map((item, index) => (
                          <div
                            key={index}
                            className="group flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/50 p-4 transition-all duration-200 hover:border-emerald-200 hover:bg-emerald-50/30 hover:shadow-sm"
                          >
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 transition-all duration-200 group-hover:scale-110 group-hover:bg-emerald-200">
                              <CheckCircle2 size={14} className="stroke-[2.5]" />
                            </div>
                            <p className="text-xs font-medium leading-relaxed text-slate-700 group-hover:text-slate-800">
                              {item}
                            </p>
                          </div>
                        ))}
                      </div>

                      {(!project.requirements || project.requirements.length === 0) && (
                        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-12">
                          <p className="text-sm font-medium text-slate-400">
                            Belum ada scope pekerjaan
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {manproTab === 'milestone' && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2 className="text-lg font-extrabold text-slate-900">Tahapan Checkpoint Pekerjaan (MANPRO)</h2>
                  <div className="mt-6 space-y-4">
                    {monitoringQuery.data?.milestones && monitoringQuery.data.milestones.length > 0 ? (
                      monitoringQuery.data.milestones.map((milestone: any, index: number) => {
                        const isExpanded = expandedMilestoneId === milestone.id;
                        const isDone = milestone.status === 'completed' || (milestone.tasks && milestone.tasks.length > 0 && milestone.tasks.every((t: any) => t.status === 'completed'));
                        return (
                          <div
                            key={milestone.id}
                            className={`overflow-hidden rounded-2xl border transition-all ${isDone ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'
                              }`}
                          >
                            <div
                              className="flex cursor-pointer items-start gap-4 p-4 hover:bg-slate-50/50"
                              onClick={() => setExpandedMilestoneId(isExpanded ? null : milestone.id)}
                            >
                              <span
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-extrabold ${isDone ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                                  }`}
                              >
                                {isDone ? <Check size={18} /> : index + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                                  <p className="text-sm font-extrabold text-slate-900 break-words">{milestone.title}</p>
                                  <div className="flex shrink-0 items-center justify-center gap-4">
                                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-extrabold ${isDone ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                                      {isDone ? 'Selesai' : 'Pending'}
                                    </span>
                                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  </div>
                                </div>
                                <div className="mt-1 flex items-center justify-between">
                                  <p className="text-xs text-slate-500 font-semibold">{milestone.tasks?.length || 0} Task(s) • Bobot: {milestone.weight}%</p>
                                </div>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                                {milestone.tasks && milestone.tasks.length > 0 ? (
                                  <div className="space-y-3">
                                    {milestone.tasks.map((task: any) => (
                                      <div key={task.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                        <div className="flex items-center gap-3">
                                          <div className={`flex h-6 w-6 items-center justify-center rounded-full ${task.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                            {task.status === 'completed' ? <Check size={12} /> : <div className="h-2 w-2 rounded-full bg-amber-500" />}
                                          </div>
                                          <span className="text-sm font-semibold text-slate-700 break-words">{task.name}</span>
                                        </div>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${task.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                          {task.progress}%
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-center text-xs font-semibold text-slate-500">Tidak ada task pada milestone ini.</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
                        <p className="text-sm font-bold text-slate-500">Belum ada tahapan pekerjaan (milestone) yang dikonfigurasi dari Backend.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {manproTab === 'foto' && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                    <div>
                      <h2 className="text-lg font-extrabold text-slate-900">Galeri Laporan Lapangan Harian</h2>
                      <p className="mt-1 text-xs text-slate-500">Foto dokumentasi pengerjaan aktual dari teknisi & temuan lapangan.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <div className="flex rounded-lg bg-slate-100 p-1">
                        <button
                          onClick={() => setPhotoFilter('semua')}
                          className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all ${photoFilter === 'semua' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          Semua
                        </button>
                        <button
                          onClick={() => setPhotoFilter('teknisi')}
                          className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all ${photoFilter === 'teknisi' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          Teknisi
                        </button>
                        <button
                          onClick={() => setPhotoFilter('owner')}
                          className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all ${photoFilter === 'owner' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          Temuan Saya
                        </button>
                      </div>
                      <button
                        onClick={() => setUploadModalState(prev => ({ ...prev, isOpen: true }))}
                        className="flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-extrabold text-white shadow-sm hover:bg-indigo-700"
                      >
                        <Camera size={14} />
                        Upload Temuan
                      </button>
                    </div>
                  </div>

                  {dailyPhotos.length === 0 ? (
                    <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                        <ImageIcon size={32} className="text-slate-400" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-700">Belum Ada Foto Lapangan</h3>
                      <p className="mt-1 max-w-sm text-xs text-slate-500">Teknisi/Mandor belum mengunggah foto progres pengerjaan untuk proyek ini.</p>
                    </div>
                  ) : (
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      {dailyPhotos.map((photo) => {
                        const isVideo = photo.mediaType === 'video';
                        return (
                        <div
                          key={photo.id}
                          onClick={() => setSelectedPhoto(photo)}
                          className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:border-indigo-400 hover:shadow-md"
                        >
                          <div className="relative h-48 overflow-hidden bg-slate-100">
                            {isVideo ? (
                              <div
                                className="h-full w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EvidenceVideoPlayer
                                  url={photo.url}
                                  className="h-full rounded-none [&_video]:h-48 [&_video]:max-h-none"
                                />
                              </div>
                            ) : (
                              <img
                                src={photo.url}
                                alt={photo.title}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            )}
                            <span className="absolute top-3 left-3 rounded-md bg-slate-900/80 px-2.5 py-1 text-[10px] font-extrabold text-white backdrop-blur-sm z-10">
                              {isVideo ? (
                                <span className="inline-flex items-center gap-1">
                                  <Film size={10} /> Video
                                </span>
                              ) : (
                                photo.category
                              )}
                            </span>
                            <span className="absolute bottom-3 right-3 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white z-10">
                              {photo.timestamp}
                            </span>
                          </div>
                          <div className="p-4">
                            <h3 className="text-xs font-extrabold text-slate-900 group-hover:text-indigo-700 break-words line-clamp-2">{photo.title}</h3>
                            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                              <MapPin size={13} className="text-slate-400" />
                              <span>{photo.locationTag}</span>
                            </p>
                            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5 text-[10px] font-bold text-slate-400">
                              <span>Oleh: {photo.reporter}</span>
                              <span className="text-indigo-600 font-extrabold">
                                {isVideo ? 'Putar video →' : 'Lihat Foto →'}
                              </span>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}



              {manproTab === 'persetujuan' && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <h2 className="text-lg font-extrabold text-slate-900">Persetujuan & Pengesahan Proyek</h2>
                      <p className="mt-1 text-xs text-slate-500">Tinjau dan berikan persetujuan untuk langkah pengerjaan proyek dari Provider.</p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    {approvalsQuery.isLoading ? (
                      <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-500">
                        Memuat data persetujuan...
                      </div>
                    ) : approvalsQuery.data?.approvals && approvalsQuery.data.approvals.filter((approval) => !isContingencyExpenseApproval(approval)).length > 0 ? (
                      approvalsQuery.data.approvals.filter((approval) => !isContingencyExpenseApproval(approval)).map((approval) => (
                        <div key={approval.id} className={`rounded-2xl border p-5 ${approval.status === 'pending' ? 'border-amber-200 bg-amber-50/50 shadow-sm' : 'border-slate-200 bg-white'}`}>
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${approval.status === 'pending' ? 'bg-amber-100 text-amber-600' : approval.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                <FileCheck2 size={20} />
                              </div>
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="text-sm font-extrabold text-slate-900 break-words flex-1 min-w-0">{approval.title}</h3>
                                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-extrabold ${approval.status === 'pending' ? 'bg-amber-200 text-amber-900' : approval.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                    {approval.status === 'pending' ? 'Menunggu Persetujuan' : approval.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                                  </span>
                                </div>
                                <p className="mt-2 text-xs text-slate-600 leading-relaxed bg-white/60 p-3 rounded-xl border border-slate-100">{approval.description}</p>


                                {approval.status === 'rejected' && approval.rejection_reason && (
                                  <p className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded-lg border border-red-100 font-medium">Alasan Penolakan: {approval.rejection_reason}</p>
                                )}
                              </div>
                            </div>
                            {approval.status === 'pending' && !isContingencyExpenseApproval(approval) && (
                              <div className="flex flex-col gap-2 shrink-0">
                                <button
                                  type="button"
                                  disabled={approveMutation.isPending}
                                  onClick={() => approveMutation.mutate(approval.id)}
                                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-extrabold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  <CheckCircle2 size={15} /> Setujui
                                </button>
                                <button
                                  type="button"
                                  disabled={rejectMutation.isPending}
                                  onClick={() => setRejectionModalState({ isOpen: true, approvalId: approval.id, reason: '' })}
                                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-xs font-extrabold text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                  <XCircle size={15} /> Tolak
                                </button>
                              </div>
                            )}
                          </div>
                          {hasRabSnapshot(approval) && (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setExpandedApprovalId(expandedApprovalId === approval.id ? null : approval.id)}
                                className="flex w-full items-center justify-between bg-slate-50 p-4 transition-colors hover:bg-slate-100"
                              >
                                <p className="font-extrabold text-slate-800 text-xs">
                                  Ringkasan Data Persetujuan (Snapshot)
                                </p>
                                {expandedApprovalId === approval.id ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                              </button>

                              {expandedApprovalId === approval.id && approval.data && (
                                <div className="p-4 border-t border-slate-200">
                                  {/* Handling for Expense Approval ('other') */}
                                  {approval.type === 'other' && approval.data && (
                                    <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                                      <p className="text-[10px] font-extrabold text-blue-800 uppercase tracking-wider mb-2">Detail Pengajuan Biaya (Expense)</p>
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div>
                                          <p className="text-xs text-slate-600 font-medium">Nominal Pengajuan</p>
                                          <p className="text-lg font-extrabold text-blue-700 mt-0.5">
                                            {approval.data.amount
                                              ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(approval.data.amount)
                                              : 'Rp 0'}
                                          </p>
                                        </div>
                                        <div>
                                          {/* Button to open receipt/evidence. Assuming receipt URL is passed, or fallback */}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const receiptUrl = approval.data?.receipt_url || approval.data?.receipt;
                                              if (!receiptUrl) {
                                                toast.error('File struk tidak dilampirkan atau format tidak didukung.');
                                                return;
                                              }
                                              setReceiptPreview({
                                                url: String(receiptUrl),
                                                title: approval.title || 'Struk pengajuan biaya',
                                              });
                                            }}
                                            className="inline-flex items-center gap-2 rounded-lg bg-white border border-blue-200 px-4 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors shadow-sm"
                                          >
                                            <FileCheck2 size={16} />
                                            Lihat struk
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {approval.data.budget !== undefined && (
                                      <div>
                                        <p className="text-slate-500 font-semibold text-[10px] uppercase tracking-wider">Total Anggaran (RAB)</p>
                                        <p className="mt-1 font-extrabold text-indigo-700 text-sm">
                                          {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(approval.data.budget)}
                                        </p>
                                      </div>
                                    )}
                                    {approval.data.milestones && (
                                      <div>
                                        <p className="text-slate-500 font-semibold text-[10px] uppercase tracking-wider">Jumlah Tahapan</p>
                                        <p className="mt-1 font-extrabold text-slate-800 text-sm">{approval.data.milestones.length} Milestone</p>
                                      </div>
                                    )}
                                  </div>

                                  {approval.data.termijns && approval.data.termijns.length > 0 && (
                                    <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                                      <p className="text-[10px] font-extrabold text-emerald-800 uppercase tracking-wider mb-2">Jadwal Pembayaran Bertahap (Termijn)</p>
                                      <div className="space-y-2">
                                        {approval.data.termijns.map((term: any, idx: number) => (
                                          <div key={idx} className="flex justify-between items-center text-xs border-b border-emerald-100/50 pb-1 last:border-0 last:pb-0">
                                            <span className="font-bold text-emerald-900">{term.title} ({term.percentage}%)</span>
                                            <span className="font-black text-emerald-700">Rp {Number(term.amount).toLocaleString('id-ID')}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {approval.data.payment_type === 'full' && (
                                    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                                      <p className="text-[10px] font-extrabold text-blue-800 uppercase tracking-wider mb-2">Metode Pembayaran</p>
                                      <div className="flex justify-between items-center text-xs">
                                        <span className="font-bold text-blue-900">Pembayaran Penuh Sekaligus (100%)</span>
                                        <span className="font-black text-blue-700">Rp {Number(approval.data.budget || 0).toLocaleString('id-ID')}</span>
                                      </div>
                                    </div>
                                  )}

                                  {/* RAB Budget Allocation Distribution Card */}
                                  {(() => {
                                    const totalBudget = Number(approval.data.budget || 0);
                                    let sdmPct = Number(approval.data.allocation_sdm || 0);
                                    let materialPct = Number(approval.data.allocation_material || 0);
                                    let contingencyPct = Number(approval.data.allocation_contingency || 0);
                                    const customAllocations: Array<{ name: string; percentage: number }> = [];

                                    const rawList = approval.data.budget_allocations || approval.data.allocation || [];
                                    const list = typeof rawList === 'string' ? JSON.parse(rawList) : rawList;

                                    if (Array.isArray(list)) {
                                      list.forEach((alloc: any) => {
                                        const id = String(alloc.id || '').toLowerCase().trim();
                                        const name = String(alloc.name || '').trim();
                                        const pct = Number(alloc.percentage || 0);

                                        const isSdm = id === 'sdm' || /sdm|tenaga|tim/i.test(name);
                                        const isMaterial = id === 'material' || /material|bahan|sparepart/i.test(name);
                                        const isContingency = id === 'contingency' || /contingency|kontingensi|darurat|cadangan/i.test(name);

                                        if (isSdm) {
                                          if (sdmPct === 0) sdmPct = pct;
                                        } else if (isMaterial) {
                                          if (materialPct === 0) materialPct = pct;
                                        } else if (isContingency) {
                                          if (contingencyPct === 0) contingencyPct = pct;
                                        } else {
                                          if (pct > 0) {
                                            customAllocations.push({ name: name || 'Alokasi Lain', percentage: pct });
                                          }
                                        }
                                      });
                                    }

                                    const sdmNominal = (totalBudget * sdmPct) / 100;
                                    const materialNominal = (totalBudget * materialPct) / 100;
                                    const contingencyNominal = (totalBudget * contingencyPct) / 100;

                                    const formatCurrency = (amount: number) =>
                                      new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

                                    const formatPct = (val: number) => (Number.isInteger(val) ? val.toString() : val.toFixed(2).replace('.', ','));

                                    return (
                                      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                                        <p className="mb-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-700">Distribusi Alokasi Biaya (RAB)</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                          <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-3 text-center">
                                            <span className="block text-[10px] font-bold text-sky-800">Tenaga Kerja (SDM)</span>
                                            <strong className="mt-0.5 block text-sm font-black text-sky-700">{formatPct(sdmPct)}%</strong>
                                            <div className="mt-1 text-[11px] font-extrabold text-slate-800">{formatCurrency(sdmNominal)}</div>
                                          </div>

                                          <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3 text-center">
                                            <span className="block text-[10px] font-bold text-blue-800">Material & Alat</span>
                                            <strong className="mt-0.5 block text-sm font-black text-blue-700">{formatPct(materialPct)}%</strong>
                                            <div className="mt-1 text-[11px] font-extrabold text-slate-800">{formatCurrency(materialNominal)}</div>
                                          </div>

                                          <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-center">
                                            <span className="block text-[10px] font-bold text-amber-800">Dana Darurat (Contingency)</span>
                                            <strong className="mt-0.5 block text-sm font-black text-amber-700">{formatPct(contingencyPct)}%</strong>
                                            <div className="mt-1 text-[11px] font-extrabold text-slate-800">{formatCurrency(contingencyNominal)}</div>
                                            {monitoringQuery.data?.contingency && (
                                              <div className="mt-2 space-y-0.5 text-[10px] font-semibold">
                                                <p className="text-amber-900/80">Terpakai {formatCurrency(Number(monitoringQuery.data.contingency.spent_amount || 0))}</p>
                                                <p className="text-emerald-800">Sisa {formatCurrency(Number(monitoringQuery.data.contingency.remaining_amount || 0))}</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {customAllocations.length > 0 && (
                                          <div className="mt-3 border-t border-slate-200 pt-2">
                                            <span className="block text-[10px] font-extrabold text-slate-600 mb-1.5">Alokasi Khusus Tambahan:</span>
                                            <div className="flex flex-wrap gap-1.5">
                                              {customAllocations.map((c, cIdx) => (
                                                <span key={cIdx} className="rounded-md bg-slate-200 px-2 py-1 text-[10px] font-bold text-slate-800">
                                                  {c.name}: {formatPct(c.percentage)}% ({formatCurrency((totalBudget * c.percentage) / 100)})
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}

                                  {/* Milestones list */}
                                  {approval.data.milestones && approval.data.milestones.length > 0 && (
                                    <div className="mt-4 border-t border-slate-100 pt-4">
                                      <p className="mb-2 text-[11px] font-extrabold text-slate-800 uppercase tracking-wider">Detail Milestone</p>
                                      <div className="space-y-2">
                                        {approval.data.milestones.map((m: any, idx: number) => (
                                          <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                            <div className="flex justify-between items-start">
                                              <p className="text-xs font-bold text-slate-700">{m.label}</p>
                                              <span className="text-[10px] font-extrabold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{m.weight}%</span>
                                            </div>
                                            {m.date && <p className="mt-1 text-[10px] text-slate-500">Tenggat: {m.date}</p>}
                                            {m.tasks && m.tasks.length > 0 && (
                                              <ul className="mt-2 list-inside list-disc space-y-1 text-[10px] text-slate-600">
                                                {m.tasks.map((t: any, tidx: number) => (
                                                  <li key={tidx}>{t.name} <span className="text-slate-400">({t.weight}%)</span></li>
                                                ))}
                                              </ul>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Team Members list */}
                                  {approval.data.team_members && approval.data.team_members.length > 0 && (
                                    <div className="mt-4 border-t border-slate-100 pt-4">
                                      <p className="mb-2 text-[11px] font-extrabold text-slate-800 uppercase tracking-wider">Detail Tim Lapangan</p>
                                      <div className="space-y-2">
                                        {approval.data.team_members.map((tm: any, idx: number) => (
                                          <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50 p-3 flex justify-between items-center">
                                            <div>
                                              <p className="text-xs font-bold text-slate-700">{tm.name}</p>
                                              <p className="mt-0.5 text-[10px] text-slate-500 capitalize">{String(tm.role).replace('_', ' ')}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Resources list */}
                                  {approval.data.resources && approval.data.resources.length > 0 && (
                                    <div className="mt-4 border-t border-slate-100 pt-4">
                                      <p className="mb-2 text-[11px] font-extrabold text-slate-800 uppercase tracking-wider">Detail Material & Vendor</p>
                                      <div className="space-y-2">
                                        {approval.data.resources.map((r: any, idx: number) => (
                                          <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50 p-3 flex justify-between items-center">
                                            <div>
                                              <p className="text-xs font-bold text-slate-700">{r.name} <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-[8px] font-bold uppercase text-slate-600">{r.type}</span></p>
                                              {r.description && <p className="mt-0.5 text-[10px] text-slate-500">{r.description}</p>}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-500">
                        Belum ada pengajuan persetujuan dari Provider.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {manproTab === 'keuangan' && (
                <div className="animate-[fadeIn_0.3s_ease-out_forwards]">
                  <OwnerFinanceTab
                    projectId={projectId as string}
                    totalBudget={Number(project.budgetCeiling || 0)}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* SIDEBAR MONITORING OWNER */}
        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[340px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-extrabold text-slate-900">
              {isMonitoringMode ? 'Mode Owner Monitoring' : 'Status Pengadaan Tender'}
            </h2>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              {isMonitoringMode
                ? 'Eksekusi pekerjaan dikelola oleh Provider Merchant. Owner menerima pembaruan berkala dan konfirmasi checkpoint.'
                : 'Owner dapat meninjau penawaran vendor dan memilih partner terbaik.'}
            </p>

            {isMonitoringMode && selectedBid && (
              <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/70 p-4 text-xs">
                <p className="font-extrabold text-indigo-900">Kontraktor Terpilih</p>
                <p className="mt-1 font-bold text-slate-800">{selectedBid.providerName}</p>
                <p className="mt-1 text-[11px] font-semibold text-indigo-700">
                  {formatCurrency(selectedBid.amount)} • Durasi {selectedBid.duration}
                </p>
              </div>
            )}

            {/* <button
              type="button"
              onClick={() => toast.success('Mengunduh Ringkasan Laporan PDF...')}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-xs font-extrabold text-blue-700 hover:bg-blue-50"
            >
              <Download size={16} /> Unduh Ringkasan PDF
            </button> */}
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-5">
            <Clock size={24} className="text-blue-700" />
            <h3 className="mt-3 font-extrabold text-slate-900 text-sm">Pusat Bantuan CS</h3>
            <p className="mt-1 text-xs text-slate-600 leading-5">Ada pertanyaan mengenai proyek Anda?</p>
            <a
              href="https://wa.me/628123456789"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-extrabold text-white hover:bg-blue-700"
            >
              <MessageSquare size={15} /> Hubungi CS Indoteknisi
            </a>
          </div>
        </aside>
      </section>

      {/* MODAL DETAIL PENAWARAN VENDOR (BID DETAILS MODAL) */}
      {selectedBidDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-sm font-extrabold text-white shadow-md shadow-blue-200">
                  {selectedBidDetail.providerName.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-extrabold text-slate-900">{selectedBidDetail.providerName}</h3>
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-extrabold text-emerald-800">
                      ✓ Vendor Terverifikasi
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Proposal Penawaran Pengadaan Proyek Owner</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedBidDetail(null)}
                className="rounded-full bg-slate-200/80 p-2 text-slate-600 hover:bg-slate-300 transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content Body */}
            <div className="max-h-[75vh] overflow-y-auto p-6 space-y-6">
              {/* Highlight Cards */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                  <p className="text-[11px] font-extrabold text-blue-700">Harga Penawaran</p>
                  <p className="mt-1 text-xl font-extrabold text-blue-900">{formatCurrency(selectedBidDetail.amount)}</p>
                  <p className="mt-1 text-[10px] text-blue-600">Plafon: {formatCurrency(project.budgetCeiling)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-extrabold text-slate-500">Estimasi Durasi Kerja</p>
                  <p className="mt-1 text-xl font-extrabold text-slate-800">{selectedBidDetail.duration}</p>
                  <p className="mt-1 text-[10px] text-slate-500">Penyelesaian Garansi Indoteknisi</p>
                </div>
                <div className="rounded-2xl border border-amber-100 bg-amber-50/80 p-4">
                  <p className="text-[11px] font-extrabold text-amber-800">Reputasi Vendor</p>
                  <p className="mt-1 flex items-center gap-1 text-xl font-extrabold text-amber-900">
                    <Star size={18} className="fill-amber-400 text-amber-400" /> 4.9 <span className="text-xs font-normal text-slate-500">(24 Proyek)</span>
                  </p>
                  <p className="mt-1 text-[10px] text-amber-700">Tingkat Ketepatan Waktu 98%</p>
                </div>
              </div>

              {/* Catatan Proposal & Metode Kerja */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h4 className="text-sm font-extrabold text-slate-900 mb-2">Catatan Proposal Vendor</h4>
                  <p className="text-xs leading-6 text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-line">
                    {selectedBidDetail.note}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h4 className="text-sm font-extrabold text-slate-900 mb-2">Metode Kerja & Rencana Eksekusi (Method Statement)</h4>
                  <p className="text-xs leading-6 text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-line">
                    {selectedBidDetail.methodStatement ||
                      '1. Pengecekan awal lokasi & pengukuran ulang elevasi.\n2. Mobilisasi material bersertifikat SNI & tenaga kerja berpengalaman.\n3. Monitoring harian melalui aplikasi Indoteknisi & foto progres rutin.\n4. Quality check bertahap sebelum pengajuan termijn.'}
                  </p>
                </div>
              </div>

              {/* Lampiran Dokumen Proposal */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h4 className="text-sm font-extrabold text-slate-900 mb-3">Lampiran Dokumen Proposal ({selectedBidDetail.documents?.length || 0})</h4>
                <div className="space-y-2">
                  {(selectedBidDetail.documents || [
                    { id: 'doc-1', name: 'RAB_Breakdown_Detail_Vendor.pdf', size: 1024000, category: 'rab' },
                    { id: 'doc-2', name: 'Company_Profile_Portofolio_Vendor.pdf', size: 2048000, category: 'portfolio' },
                  ]).map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700 font-bold text-xs">
                          PDF
                        </div>
                        <span className="text-xs font-extrabold text-slate-800">{doc.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toast.success(`Mengunduh ${doc.name}...`)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-50"
                      >
                        <Download size={13} /> Unduh
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setSelectedBidDetail(null)}
                className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-extrabold text-slate-700 hover:bg-slate-100"
              >
                Tutup
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleChatVendor(selectedBidDetail.userId || selectedBidDetail.providerId, project.id)}
                  disabled={createChannelMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-extrabold text-slate-800 hover:bg-slate-100 disabled:opacity-50"
                >
                  <MessageSquare size={15} />
                  {createChannelMutation.isPending ? 'Membuka...' : 'Chat & Negosiasi'}
                </button>
                {selectedBidDetail.status === 'submitted' && (
                  <button
                    type="button"
                    disabled={isAcceptingBid}
                    onClick={() => {
                      acceptBid(selectedBidDetail.id);
                      setSelectedBidDetail(null);
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-extrabold text-white shadow-md shadow-blue-100 hover:bg-blue-700"
                  >
                    <ShieldCheck size={16} /> Pilih & Terima Vendor
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECTION MODAL */}
      {rejectionModalState.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 text-red-600">
                  <XCircle size={20} />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900">Tolak Persetujuan</h3>
              </div>
              <button
                type="button"
                onClick={() => setRejectionModalState((s) => ({ ...s, isOpen: false }))}
                className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm font-bold text-slate-700">Berikan alasan penolakan agar Provider dapat melakukan revisi:</p>
              <textarea
                className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-900 outline-none focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-100"
                rows={4}
                placeholder="Contoh: Tolong perbaiki daftar RAB pada bagian material, harganya tidak sesuai..."
                value={rejectionModalState.reason}
                onChange={(e) => setRejectionModalState((s) => ({ ...s, reason: e.target.value }))}
              />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
              <button
                type="button"
                onClick={() => setRejectionModalState((s) => ({ ...s, isOpen: false }))}
                className="rounded-xl px-5 py-2.5 text-xs font-extrabold text-slate-600 hover:bg-slate-200"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={!rejectionModalState.reason.trim() || rejectMutation.isPending}
                onClick={() => rejectMutation.mutate({ approvalId: rejectionModalState.approvalId, reason: rejectionModalState.reason })}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-xs font-extrabold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
              >
                Konfirmasi Penolakan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Temuan Modal */}
      <Modal
        isOpen={uploadModalState.isOpen}
        onClose={() => !uploadModalState.isUploading && setUploadModalState(prev => ({ ...prev, isOpen: false }))}
        title="Upload Foto Temuan Lapangan"
        size="md"
        showCloseButton={!uploadModalState.isUploading}
      >
        <form onSubmit={handleUploadPhoto} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">Pilih Foto <span className="text-red-500">*</span></label>
            <input
              type="file"
              accept="image/*"
              required
              className="w-full rounded-xl border border-slate-300 p-2 text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-xs file:font-extrabold file:text-indigo-700 hover:file:bg-indigo-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              onChange={(e) => setUploadModalState(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
              disabled={uploadModalState.isUploading}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">Judul Temuan <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              placeholder="Contoh: Keramik retak di dapur"
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={uploadModalState.title}
              onChange={(e) => setUploadModalState(prev => ({ ...prev, title: e.target.value }))}
              disabled={uploadModalState.isUploading}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">Lokasi / Titik Temuan</label>
            <input
              type="text"
              placeholder="Contoh: Dapur Belakang"
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={uploadModalState.location}
              onChange={(e) => setUploadModalState(prev => ({ ...prev, location: e.target.value }))}
              disabled={uploadModalState.isUploading}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">Catatan Tambahan</label>
            <textarea
              placeholder="Jelaskan detail temuan Anda..."
              rows={3}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={uploadModalState.description}
              onChange={(e) => setUploadModalState(prev => ({ ...prev, description: e.target.value }))}
              disabled={uploadModalState.isUploading}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setUploadModalState(prev => ({ ...prev, isOpen: false }))}
              className="rounded-xl px-5 py-2.5 text-sm font-extrabold text-slate-600 hover:bg-slate-100"
              disabled={uploadModalState.isUploading}
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={uploadModalState.isUploading || !uploadModalState.file || !uploadModalState.title}
              className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-extrabold text-white transition-all hover:bg-indigo-700 disabled:opacity-50"
            >
              {uploadModalState.isUploading ? 'Mengunggah...' : 'Upload Foto'}
            </button>
          </div>
        </form>
      </Modal>

      <ReceiptPreviewModal receipt={receiptPreview} onClose={() => setReceiptPreview(null)} />

      <Modal
        isOpen={selectedPhoto !== null}
        onClose={() => setSelectedPhoto(null)}
        size="full"
        showCloseButton
        title={
          selectedPhoto && (
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {selectedPhoto.title}
              </h2>
              <p className="text-sm text-slate-500">
                {selectedPhoto.category}
              </p>
            </div>
          )
        }
        className="overflow-hidden"
        contentClassName="p-0"
      >
        {selectedPhoto && (
          <div className="grid h-full lg:grid-cols-[1fr_360px]">
            {/* Media: foto atau video (V8) */}
            <div className="flex items-center justify-center bg-slate-900 p-2 sm:p-4">
              {selectedPhoto.mediaType === 'video' ? (
                <EvidenceVideoPlayer
                  url={selectedPhoto.url}
                  className="w-full max-w-4xl"
                />
              ) : (
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.title}
                  className="max-h-[82vh] w-full object-contain"
                />
              )}
            </div>

            {/* Informasi */}
            <div className="border-l border-slate-200 bg-white p-6">
              <span className="inline-flex rounded-lg bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                {selectedPhoto.category}
              </span>

              <h3 className="mt-4 text-2xl font-bold text-slate-900">
                {selectedPhoto.title}
              </h3>

              <div className="mt-8 space-y-5 text-sm">
                <div>
                  <p className="font-semibold text-slate-700">
                    Lokasi
                  </p>
                  <p className="mt-1 text-slate-500">
                    {selectedPhoto.locationTag}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-slate-700">
                    Dilaporkan Oleh
                  </p>
                  <p className="mt-1 text-slate-500">
                    {selectedPhoto.reporter}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-slate-700">
                    Waktu Upload
                  </p>
                  <p className="mt-1 text-slate-500">
                    {selectedPhoto.timestamp}
                  </p>
                </div>

                {selectedPhoto.note && (
                  <div>
                    <p className="font-semibold text-slate-700">
                      Keterangan
                    </p>
                    <p className="mt-1 leading-6 text-slate-500">
                      {selectedPhoto.note}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ProyekSayaDetail;
